// offlineQueue.js — bandeja de salida para las mutaciones críticas del técnico
// (finalizar visita, cierre de ruta). Si al enviar no hay red, la petición se
// guarda aquí y se reintenta sola al reconectar, en orden y sin duplicar.
//
// Persistencia: localStorage (los payloads son JSON pequeños; IndexedDB sería
// sobreingeniería). Idempotencia: cada entrada lleva un `clientRequestId` que
// el backend usa para no aplicar dos veces la misma operación.
//
// Regla de flush: una entrada se elimina de la cola en cuanto el servidor
// DEVUELVE UNA RESPUESTA HTTP (2xx → ok; 4xx/5xx → se marca "failed" para
// revisión manual y se saca del reintento automático). Solo los errores de
// RED (fetch rechaza) mantienen la entrada para reintentar. Así el único
// riesgo de doble envío es "respuesta perdida en tránsito", que además el
// `clientRequestId` neutraliza en el servidor.
import { authService } from './authService';

const STORAGE_KEY = 'zapata_offline_queue_v1';
const MAX_ATTEMPTS = 8;

let listeners = new Set();
let flushing = false;
let flushTimer = null;

// ── Persistencia ────────────────────────────────────────────────────────────
function read() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function write(entries) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    /* almacenamiento lleno o modo privado — la cola vive solo en memoria esta sesión */
  }
  emit();
}

function emit() {
  const snap = getState();
  listeners.forEach(fn => { try { fn(snap); } catch { /* noop */ } });
}

// ── API pública ────────────────────────────────────────────────────────────
export function getState() {
  const entries = read();
  return {
    entries,
    pending: entries.filter(e => e.status !== 'failed').length,
    failed:  entries.filter(e => e.status === 'failed').length,
    flushing,
  };
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(getState());
  return () => listeners.delete(fn);
}

function uuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch { /* noop */ }
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Encola una mutación. `clientRequestId` se genera si no se pasa y se inyecta
 * en el body (para que el backend deduplique).
 * @returns {{ id: string, clientRequestId: string }}
 */
export function enqueue({ kind, url, method = 'POST', body = {}, clientRequestId }) {
  if (!url) throw new Error('offlineQueue.enqueue: falta url');
  const crid = clientRequestId || uuid();
  const entry = {
    id: uuid(),
    kind: kind || 'generic',
    url,
    method,
    body: { ...body, clientRequestId: crid },
    clientRequestId: crid,
    status: 'pending',
    attempts: 0,
    lastError: '',
    createdAt: new Date().toISOString(),
  };
  const entries = read();
  entries.push(entry);
  write(entries);
  // Intenta enviar cuanto antes (si hay red).
  scheduleFlush(400);
  return { id: entry.id, clientRequestId: crid };
}

export function removeEntry(id) {
  write(read().filter(e => e.id !== id));
}

export function retryEntry(id) {
  const entries = read().map(e =>
    e.id === id ? { ...e, status: 'pending', attempts: 0, lastError: '' } : e
  );
  write(entries);
  scheduleFlush(0);
}

export function clearFailed() {
  write(read().filter(e => e.status !== 'failed'));
}

function scheduleFlush(delay = 0) {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => { flushTimer = null; flush(); }, delay);
}

/**
 * Envía las entradas pendientes en orden. Se detiene en el primer error de red
 * (para no romper el orden) y sigue en la siguiente oportunidad.
 */
export async function flush() {
  if (flushing) return getState();
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return getState();

  const pendientes = read().filter(e => e.status !== 'failed');
  if (pendientes.length === 0) return getState();

  flushing = true;
  emit();

  try {
    for (const entry of pendientes) {
      // Releer por si otra pestaña la quitó mientras tanto.
      if (!read().some(e => e.id === entry.id)) continue;

      let resp;
      try {
        resp = await fetch(entry.url, {
          method: entry.method,
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify(entry.body),
        });
      } catch (netErr) {
        // Error de RED: no llegó al servidor. Mantener y reintentar luego.
        bump(entry.id, netErr.message || 'sin conexión', /* keep */ true);
        break; // preservar orden
      }

      // Llegó respuesta HTTP → la operación se procesó (o se rechazó). En
      // cualquier caso, fuera del reintento automático.
      if (resp.ok) {
        removeEntry(entry.id);
      } else {
        let msg = `HTTP ${resp.status}`;
        try { const j = await resp.json(); if (j && j.error) msg = j.error; } catch { /* noop */ }
        markFailed(entry.id, msg);
      }
    }
  } finally {
    flushing = false;
    emit();
  }
  return getState();
}

function bump(id, error, keep) {
  const entries = read().map(e => {
    if (e.id !== id) return e;
    const attempts = e.attempts + 1;
    if (!keep || attempts >= MAX_ATTEMPTS) {
      return { ...e, status: 'failed', attempts, lastError: error };
    }
    return { ...e, attempts, lastError: error };
  });
  write(entries);
}

function markFailed(id, error) {
  write(read().map(e => (e.id === id ? { ...e, status: 'failed', lastError: error } : e)));
}

// ── Disparadores automáticos ───────────────────────────────────────────────
let installed = false;
export function installOfflineQueue() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('online', () => scheduleFlush(300));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') scheduleFlush(300);
  });
  // Reintento periódico suave mientras la app está abierta y hay pendientes.
  setInterval(() => {
    const { pending } = getState();
    if (pending > 0) scheduleFlush(0);
  }, 30_000);

  // Al arrancar, intenta vaciar lo que quedara de una sesión anterior.
  scheduleFlush(1500);
}

export const __STORAGE_KEY = STORAGE_KEY;
